'use client'

import { type ReactNode, useEffect, useRef, useState } from 'react'

interface LenisRevealProps {
  children: ReactNode
  className?: string
  type?: 'fade' | 'slide' | 'staggered' | 'scale' | 'parallax'
  direction?: 'up' | 'down' | 'left' | 'right'
  delay?: number
  duration?: number
  staggerDelay?: number
  threshold?: number
  rootMargin?: string
  once?: boolean
  /**
   * Opt-in scroll-reveal behavior. When false (default), content renders
   * visible immediately — crawlers, AI bots, and users on slow connections
   * see content on first paint. When true, content starts hidden and fades
   * in via IntersectionObserver when scrolled into view. Default flipped
   * 2026-05-23 after /tools page shipped with empty above-fold blanks and
   * AI/SEO crawlers couldn't see hidden content.
   */
  revealOnScroll?: boolean
}

export function LenisReveal({
  children,
  className = '',
  type = 'fade',
  direction = 'up',
  delay = 0,
  threshold = 0.15,
  rootMargin = '0px 0px -40px 0px',
  once = true,
  revealOnScroll = false,
}: LenisRevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(!revealOnScroll)

  useEffect(() => {
    if (!revealOnScroll) return
    const el = ref.current
    if (!el) return

    // Respect prefers-reduced-motion
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    if (prefersReduced) {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Apply delay before revealing
          if (delay > 0) {
            setTimeout(() => setIsVisible(true), delay)
          } else {
            setIsVisible(true)
          }
          if (once) observer.unobserve(el)
        } else if (!once) {
          setIsVisible(false)
        }
      },
      { threshold, rootMargin }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [revealOnScroll, delay, threshold, rootMargin, once])

  const classes = [
    'lenis-reveal-element',
    type ? `lenis-reveal-${type}` : '',
    direction && direction !== 'up' ? `lenis-reveal-${direction}` : '',
    isVisible ? 'lenis-reveal-visible' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={ref} className={classes}>
      {children}
    </div>
  )
}

// Specialized components
export function LenisFadeIn({
  children,
  className = '',
  ...rest
}: LenisRevealProps) {
  return (
    <LenisReveal className={className} type='fade' {...rest}>
      {children}
    </LenisReveal>
  )
}

export function LenisSlideUp({
  children,
  className = '',
  ...rest
}: LenisRevealProps) {
  return (
    <LenisReveal className={className} type='slide' direction='up' {...rest}>
      {children}
    </LenisReveal>
  )
}

export function LenisStaggered({
  children,
  className = '',
  staggerDelay = 100,
  ...rest
}: LenisRevealProps) {
  return (
    <LenisReveal
      className={`${className} lenis-stagger-${staggerDelay}`}
      type='staggered'
      {...rest}
    >
      {children}
    </LenisReveal>
  )
}

export function LenisScale({
  children,
  className = '',
  ...rest
}: LenisRevealProps) {
  return (
    <LenisReveal className={className} type='scale' {...rest}>
      {children}
    </LenisReveal>
  )
}

export function LenisParallax({
  children,
  className = '',
  ...rest
}: LenisRevealProps) {
  return (
    <LenisReveal className={className} type='parallax' {...rest}>
      {children}
    </LenisReveal>
  )
}
